/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavItem
import { item, itemContent, itemArrow } from "examples/Sidenav/styles/sidenavItem";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import MDTypography from "components/MDTypography";

function SidenavItem({ color, name, percent, icon, active, nested, device_id, geofence, alert, children, open, isNameNeed = true, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  function rewriteName(name, percent) {
    if(percent){
      let ops = ['-', null, 'null'].includes(percent) ? '' : '%'
      return `${name.slice(0, 15)}: ${percent}${ops}`
    }
    return name.slice(0, 15)
  }

  function rewritePercent(percent){
    let ops = ['-', null, 'null'].includes(percent) ? '' : '%';
    return `${percent}${ops}`
  }
  return (
    <>
      <ListItem
        {...rest}
        component="li"
        sx={(theme) => ({
          ...item(theme, { active, color, transparentSidenav, whiteSidenav, darkMode, isNameNeed }),
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        })}
      >
        <MDBox
          sx={(theme) =>
            itemContent(theme, {
              active,
              miniSidenav,
              name,
              open,
              nested,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              isNameNeed
            })
          }
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            overflow: 'hidden', 
            boxSizing: 'border-box', 
            position: 'relative',
            width: window.innerWidth > 1200 ? '300px' : '100%' 
          }}
        >
          {icon ? (<i className={icon} style={{ fontSize: '20px', color: 'white', flexShrink: 0 }}></i>) : ''}
          <div style={{ flex: '1 1 auto', overflow: 'hidden', fontSize: '1rem', fontWeight: '300', textAlign: 'left', whiteSpace: 'nowrap', minWidth: 0, maxWidth: '100%' }}>
            <MDTypography variant="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}>{rewriteName(name)} {percent ? <>: <span id={`percent_${device_id}`} title={rewritePercent(percent)}>{rewritePercent(percent)}</span></> : ''}</MDTypography>
          </div>
          <i id={`alert_noti_${device_id}`} title={alert?.AlertType?.description} className={alert?.AlertType?.Icon?.class_name ?? ""} style={{ fontSize: '20px', color: `${alert?.AlertType?.BadgeDetail?.font_color_hex ?? ''}`, flexShrink: 0 }}></i>
          <div id={`geofence_${device_id}`} style={{ width: '15px', height: '15px', borderRadius: '50%', display: 'inline-block', backgroundColor: geofence ? geofence?.color : '', flexShrink: 0, marginLeft: '4px' }} title={geofence ? geofence?.name : ''}></div>
          {children && (
            <Icon
              component="i"
              sx={(theme) =>
                itemArrow(theme, { open, miniSidenav, transparentSidenav, whiteSidenav, darkMode })
              }
            >
              expand_less
            </Icon>
          )}
        </MDBox>
      </ListItem>
      {children && (
        <Collapse in={open} timeout="auto" unmountOnExit {...rest}>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Setting default values for the props of SidenavItem
SidenavItem.defaultProps = {
  color: "info",
  active: false,
  nested: false,
  children: false,
  open: false,
};

// Typechecking props for the SidenavItem
SidenavItem.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  name: PropTypes.string.isRequired,
  active: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  nested: PropTypes.bool,
  children: PropTypes.node,
  open: PropTypes.bool,
};

export default SidenavItem;
